// JOBE-0098.5: v1 help sheet listing the shell-level + within-palette
// chords. Opened by `?` (registered by Shell.jsx). Uses the existing
// `.scrim` / `.modal` primitives so no new CSS framework is needed.
//
// v1 is a flat two-section list grouped by `OMNI_CHORDS[].context`. A
// richer pass (sectioned by route, inline examples) is a future
// refinement per the spec.
//
// JOBE-0165.5: extended with a route-based filter — the W5 page-local
// context `promptops-agent` shows only on `/promptops/agent/:name` routes.
// We read `window.location.pathname` directly rather than `useLocation()`
// so the help sheet remains usable in tests/mounts without a Router (its
// existing test suite mounts it bare).

import { useEffect, useMemo } from 'react';
import { OMNI_CHORDS } from '../../lib/omniChords.js';

const SECTION_TITLES = {
  shell: 'Shell',
  palette: 'Inside the palette',
  'promptops-agent': 'PromptOps Agent (W5)',
};

const PROMPTOPS_AGENT_PATH = /^\/promptops\/agent\//;

export function OmniChordHelpSheet({ onClose }) {
  const sections = useMemo(() => {
    const pathname = (typeof window !== 'undefined' && window.location?.pathname) || '';
    const isPromptOpsAgent = PROMPTOPS_AGENT_PATH.test(pathname);
    const visible = OMNI_CHORDS.filter((c) => {
      if (c.context === 'promptops-agent') return isPromptOpsAgent;
      return true;
    });
    const order = ['shell', 'palette', 'promptops-agent'];
    return order
      .map((ctx) => [ctx, visible.filter((c) => c.context === ctx)])
      .filter(([, rows]) => rows.length > 0);
  }, []);

  // Local Esc handler. The shell-level handler also closes via
  // setHelpSheetOpen(false) on Esc; this keeps the modal self-contained
  // when consumed in isolation (e.g. tests).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="scrim" data-testid="omni-help-sheet" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-label="Keyboard shortcuts"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <span>Keyboard shortcuts</span>
          <span className="grow" />
          <button className="x" type="button" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="omni-help-body">
          {sections.map(([ctx, rows]) => (
            <section key={ctx} className="omni-help-section">
              <h3 className="omni-help-section-title">{SECTION_TITLES[ctx] || ctx}</h3>
              <ul className="omni-help-list">
                {rows.map((row) => (
                  <li key={row.chord + row.label} className="omni-help-row">
                    <kbd className="omni-help-chord">{row.chord}</kbd>
                    <span className="omni-help-label">{row.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
